import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string[];
  ogType?: string;
  /** Public (indexable) pages show the real translated title in the browser tab. */
  isPublic?: boolean;
}

/** Routes whose translated title may be exposed in the browser tab / SEO tags. */
const PUBLIC_ROUTES = ["/", "/auth", "/explorar", "/privacidade", "/termos", "/ajuda", "/bera-ia"];

export const isPublicRoute = (pathname: string): boolean =>
  PUBLIC_ROUTES.includes(pathname) || pathname.startsWith("/property/");

/** Translated keyword list for the current language. */
export const getKeywords = (t: (key: string) => string): string[] =>
  (t("seo.keywords") || "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);

const BASE_URL = "https://www.berastays.com";
const SITE_NAME = "Berastays";
const DEFAULT_DESCRIPTION = "Plataforma de reservas de alojamento local em Angola. Encontre estadias únicas, faça ofertas personalizadas e reserve de forma segura.";

const getSEORoutes = (t: (key: string) => string): Record<string, SEOConfig> => ({
  "/": {
    title: t('seo.homeTitle') || `${SITE_NAME} - Reservas de Alojamento Local em Angola`,
    description: t('seo.homeDesc') || DEFAULT_DESCRIPTION,
    ogType: "website",
  },
  "/auth": {
    title: t('seo.authTitle') || `Entrar ou Cadastrar - ${SITE_NAME}`,
    description: t('seo.authDesc') || "Acesse sua conta ou crie uma nova para começar a reservar alojamentos incríveis em Angola.",
  },
  "/explorar": {
    title: t('seo.exploreTitle') || `Explorar Propriedades - ${SITE_NAME}`,
    description: t('seo.exploreDesc') || "Descubra centenas de propriedades únicas em Angola. Filtrar por localização, preço, tipo de propriedade e comodidades.",
    ogType: "website",
  },
  "/ofertas/nova": {
    title: t('seo.offerTitle') || `Fazer Oferta Personalizada - ${SITE_NAME}`,
    description: t('seo.offerDesc') || "Crie uma oferta personalizada com suas preferências e receba propostas de anfitriões em tempo real.",
  },
  "/reservas": {
    title: t('seo.bookingsTitle') || `Minhas Reservas - ${SITE_NAME}`,
    description: t('seo.bookingsDesc') || "Gerencie suas reservas ativas, histórico de estadias e comunicação com anfitriões.",
  },
  "/favoritos": {
    title: t('seo.favoritesTitle') || `Favoritos - ${SITE_NAME}`,
    description: t('seo.favoritesDesc') || "Acesse rapidamente suas propriedades favoritas salvas para futuras reservas.",
  },
  "/ofertas/recebidas": {
    title: t('seo.receivedOffersTitle') || `Ofertas Recebidas - ${SITE_NAME}`,
    description: t('seo.receivedOffersDesc') || "Visualize e responda às ofertas de hóspedes interessados em suas propriedades.",
  },
  "/host-dashboard": {
    title: t('seo.hostDashTitle') || `Painel do Anfitrião - ${SITE_NAME}`,
    description: t('seo.hostDashDesc') || "Gerencie suas propriedades, reservas, ganhos e comunicação com hóspedes em um só lugar.",
  },
  "/add-property": {
    title: t('seo.addPropTitle') || `Adicionar Propriedade - ${SITE_NAME}`,
    description: t('seo.addPropDesc') || "Publique sua propriedade na plataforma e comece a receber reservas de hóspedes verificados.",
  },
  "/perfil": {
    title: t('seo.profileTitle') || `Meu Perfil - ${SITE_NAME}`,
    description: t('seo.profileDesc') || "Atualize suas informações pessoais, foto de perfil e preferências da conta.",
  },
  "/configuracoes": {
    title: t('seo.settingsTitle') || `Configurações - ${SITE_NAME}`,
    description: t('seo.settingsDesc') || "Gerencie preferências de notificações, privacidade, segurança e outras configurações da conta.",
  },
  "/notificacoes": {
    title: t('seo.notificationsTitle') || `Notificações - ${SITE_NAME}`,
    description: t('seo.notificationsDesc') || "Visualize todas as suas notificações, mensagens e atualizações importantes.",
  },
  "/privacidade": {
    title: t('seo.privacyTitle') || `Política de Privacidade - ${SITE_NAME}`,
    description: t('seo.privacyDesc') || "Saiba como a Berastays coleta, usa, protege e compartilha seus dados pessoais ao utilizar a plataforma.",
    ogType: "website",
  },
  "/termos": {
    title: t('seo.termsTitle') || `Termos e Condições - ${SITE_NAME}`,
    description: t('seo.termsDesc') || "Leia os termos de uso da plataforma Berastays, incluindo regras de reserva, pagamentos, cancelamentos e responsabilidades.",
    ogType: "website",
  },
  "/ajuda": {
    title: t('seo.helpTitle') || `Central de Ajuda - ${SITE_NAME}`,
    description: t('seo.helpDesc') || "Encontre respostas sobre criação de conta, verificação de identidade, reservas, pagamentos, afiliados e muito mais.",
    ogType: "website",
  },
  "/bera-ia": {
    title: t('seo.aiTitle') || `Bera - Assistente Virtual ${SITE_NAME}`,
    description: t('seo.aiDesc') || "Converse com nosso assistente inteligente para encontrar propriedades, fazer reservas e obter ajuda instantânea.",
  },
  "/afiliado-auth": {
    title: t('seo.affiliateAuthTitle') || `Portal de Afiliados - ${SITE_NAME}`,
    description: t('seo.affiliateAuthDesc') || "Acesse o portal de afiliados para gerenciar links, comissões e ganhos.",
  },
  "/affiliate-panel": {
    title: t('seo.affiliatePanelTitle') || `Painel do Afiliado - ${SITE_NAME}`,
    description: t('seo.affiliatePanelDesc') || "Gerencie seus links de afiliado, acompanhe cliques, conversões e solicite saques.",
  },
});

// Função para obter configuração SEO de uma rota
export const getSEOConfig = (pathname: string, t: (key: string) => string): SEOConfig => {
  const routes = getSEORoutes(t);
  
  if (routes[pathname]) {
    return {
      ...routes[pathname],
      keywords: getKeywords(t),
      isPublic: isPublicRoute(pathname),

      canonical: `${BASE_URL}${pathname}`,
    };
  }

  // Trata rotas dinâmicas
  if (pathname.startsWith("/property/")) {
    return {
      title: t('seo.propertyTitle') || `Detalhes da Propriedade - ${SITE_NAME}`,
      description: t('seo.propertyDesc') || "Veja fotos, comodidades, avaliações e disponibilidade desta propriedade única em Angola.",
      keywords: getKeywords(t),
      isPublic: isPublicRoute(pathname),
      canonical: `${BASE_URL}${pathname}`,
    };
  }

  if (pathname.startsWith("/booking/")) {
    return {
      title: t('seo.bookingDetailTitle') || `Detalhes da Reserva - ${SITE_NAME}`,
      description: t('seo.bookingDetailDesc') || "Visualize os detalhes completos da sua reserva, incluindo datas, pagamento e informações de contato.",
      keywords: getKeywords(t),
      isPublic: isPublicRoute(pathname),
      canonical: `${BASE_URL}${pathname}`,
    };
  }

  if (pathname.startsWith("/admin/")) {
    return {
      title: t('seo.adminDashTitle') || `Painel Admin - ${SITE_NAME}`,
      description: t('seo.adminDashDesc') || "Área administrativa da plataforma Berastays.",
      keywords: getKeywords(t),
      isPublic: isPublicRoute(pathname),
      canonical: `${BASE_URL}${pathname}`,
    };
  }

  if (pathname.startsWith("/affiliate/")) {
    return {
      title: t('seo.affiliateTitle') || `Painel de Afiliado - ${SITE_NAME}`,
      description: t('seo.affiliateDesc') || "Gerencie sua conta de afiliado e acompanhe seus ganhos.",
      keywords: getKeywords(t),
      isPublic: isPublicRoute(pathname),
      canonical: `${BASE_URL}${pathname}`,
    };
  }

  // Fallback para rotas não mapeadas
  return {
    title: SITE_NAME,
    description: t('seo.homeDesc') || DEFAULT_DESCRIPTION,
    keywords: getKeywords(t),
    isPublic: isPublicRoute(pathname),
    canonical: `${BASE_URL}${pathname}`,
  };

};

// Hook para usar SEO config na rota atual
export const useSEOConfig = (): SEOConfig => {
  const location = useLocation();
  const { t } = useTranslation();
  return getSEOConfig(location.pathname, t);
};

// Função helper para gerar título completo
export const getFullTitle = (pageTitle?: string): string => {
  if (!pageTitle) return SITE_NAME;
  return pageTitle.includes(SITE_NAME) ? pageTitle : `${pageTitle} - ${SITE_NAME}`;
};

// Constantes exportadas
export { BASE_URL, SITE_NAME, DEFAULT_DESCRIPTION };
